import { IDB_WORKS } from '#pplx/frontend/browser-storage/indexedDB.js';
import { FetcherError } from '#pplx/frontend/fetcher/errors.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { QUERY_CACHE_KEY } from '#pplx/frontend/react-query/consts.js';
import { setEncryptedQueryCacheValue } from '#pplx/frontend/react-query/encryptedQueryCache.js';
import { persistQueryCache } from '#pplx/frontend/react-query/internal/queryCachePersistence.js';
import { getQueryCacheStore } from '#pplx/frontend/react-query/queryCacheStore.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { hydrate, isServer, MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { del, delMany, keys } from 'idb-keyval';
import { noop } from 'lodash-es';
import { isErrorLike, serializeError } from 'serialize-error';
let browserQueryClient = undefined;
let queryCachePersistence = undefined;
/**
 * Each version of the app has its own query cache. This prevents old tabs with older
 * data versions and schemas from overlapping with new tabs.
 * @param version The version of the app
 * @returns The versioned key for the query cache
 */
function getVersionedKey(version) {
    return `${QUERY_CACHE_KEY}-${version}`;
}
/**
 * Get a singleton QueryClient instance for the current environment.
 * @returns The ReactQuery instance
 */
export function getQueryClient({ buildVersion, allowPersist, }) {
    if (isServer) {
        // Server: always make a new query client
        return makeQueryClient({ allowPersist: false });
    }
    else {
        // Browser: make a new query client if we don't already have one
        // This is very important, so we don't re-make a new client if React
        // suspends during the initial render. This may not be needed if we
        // have a suspense boundary BELOW the creation of the query client
        if (!browserQueryClient) {
            const key = getVersionedKey(buildVersion);
            QueryClientHelper.setVersion(key);
            browserQueryClient = makeQueryClient({
                allowPersist: allowPersist && typeof indexedDB !== 'undefined',
            });
        }
        const initialState = window.__PPL_INITIAL_SERVER_DATA__;
        if (initialState) {
            hydrate(browserQueryClient, initialState);
        }
        return browserQueryClient;
    }
}
function makeQueryClient({ allowPersist }) {
    const client = new QueryClient({
        queryCache: new QueryCache({
            onError: (err) => {
                const error = err instanceof FetcherError
                    ? err
                    : new FetcherError('FETCHER_UNKNOWN_ERROR', {
                        details: {
                            cause: serializeError(err),
                        },
                        cause: isErrorLike(err) ? err : undefined,
                    });
                logger.error(error);
            },
        }),
        mutationCache: new MutationCache({
            onError: (err) => {
                logger.error('Mutation error', err);
            },
        }),
        defaultOptions: {
            queries: {
                staleTime: QueryClientHelper.STALE_TIME,
                gcTime: QueryClientHelper.GC_TIME,
                refetchOnReconnect: true, // Stale time is infinity, so these only run when overridden or query is intentionally invalidated
                refetchOnMount: true,
                refetchOnWindowFocus: true,
                retry: QueryClientHelper.retryQuery,
                retryDelay: QueryClientHelper.retryDelay,
                throwOnError: false,
                networkMode: allowPersist ? 'offlineFirst' : 'online',
            },
            mutations: {
                gcTime: QueryClientHelper.GC_TIME,
                retry: QueryClientHelper.retryMutation,
                retryDelay: QueryClientHelper.retryDelay,
                throwOnError: false,
                networkMode: 'online',
            },
        },
    });
    client.setDefaultOptions({
        queries: {
            ...client.getDefaultOptions().queries,
            queryFn: async ({ queryKey }) => client.getQueryData(queryKey) ?? null,
        },
        mutations: client.getDefaultOptions().mutations,
    });
    // Subscribe to query updates for IDB persistence, but only after we've
    // verified that IndexedDB is actually functional (not just present).
    if (allowPersist && !isServer) {
        void IDB_WORKS.then((works) => {
            if (!works)
                return;
            queryCachePersistence = persistQueryCache(client.getQueryCache(), {
                getStore: getQueryCacheStore,
                write: (persistKey, data, store) => {
                    setEncryptedQueryCacheValue(persistKey, data, store).catch(noop); // intentionally run away
                },
                remove: (persistKey, store) => {
                    del(persistKey, store).catch(noop); // intentionally run away
                },
            });
        });
    }
    return client;
}
/**
 * Clears the persisted cache for all environments.
 */
export async function clearPersistedCache() {
    // Deferred writes would otherwise restore data that this clear removes (e.g. on sign-out).
    queryCachePersistence?.discardPendingWrites();
    try {
        if (await IDB_WORKS) {
            const store = getQueryCacheStore();
            await delMany(await keys(store), store);
        }
    }
    catch {
        // noop
    }
}
//# sourceMappingURL=queryClient.js.map